import { useRef, useState } from 'react';

import { useMutation } from '@apollo/client';
import { useAmplitude } from '@crehana/analytics';
import { CREHANA_DOMAIN_PRD } from '@crehana/constants';
import { customLocalStorage, getNextUrl } from '@crehana/utils';
// biome-ignore lint/style/useImportType: legacy import, will be migrated
import { MeObject } from '@crehana/web';

import SOCIAL_LOGIN_MUTATION from '@/shared/graphql/AuthFormSocialloginMutation.auth.graphql';
// biome-ignore lint/style/useImportType: legacy import, will be migrated
import {
  AuthFormSocialloginMutation,
  AuthFormSocialloginMutationVariables,
} from '@/shared/graphql/types/AuthFormSocialloginMutation';
import {
  loadGoogleAndFacebookScripts,
  sendRegisterTrackToGoogleAdsAndAnalytics,
} from '@/shared/utils';

type SocialLoginVariables = {
  isJwtAuth?: boolean;
  token: string;
  nextUrl?: string;
  backend: string;
  referFriendId?: number;
  friendInvitationId?: string;
};

export type UseSocialAuthOptions = {
  friendInvitationId?: string;
  referFriendId?: string;
  nextUrl?: string;
  onSuccess?: (params: {
    redirect?: string | null;
    user?: MeObject;
    isNewUser?: boolean | null;
    graphqlResponse?: AuthFormSocialloginMutation['socialLogin'];
  }) => void;
  onFinishToLoadScripts?: (success: boolean) => void;
  /**
   * Pass the page, section or component where this component is used.
   * This prop is used to send extra data to the Amplitude
   */
  isRenderedIn?: string;
  getNextUrl?: () => string | undefined;
  isJwtAuth?: boolean;
  hideFacebookLink?: boolean;
};

export const googleFallbackUrl = '/login/google-oauth2/';
export const facebookFallbackUrl = '/login/facebook/';

let idCounter = 0;
const genId = () => {
  idCounter += 1;
  return idCounter;
};

export const useSocialAuth = ({
  friendInvitationId,
  referFriendId,
  nextUrl: nextUrlProp,
  onSuccess,
  onFinishToLoadScripts,
  getNextUrl: getNextUrlProp,
  isJwtAuth,
  hideFacebookLink,
}: UseSocialAuthOptions = {}) => {
  const { logEvent } = useAmplitude();
  const gmailButtonRef = useRef<HTMLButtonElement>(null);
  const gmailHTMLButtonId = useRef(`customGoogleButton-${genId()}`);
  const [processingSocialAuth, setProcessingSocialAuth] = useState(false);
  const [socialLogin] = useMutation<
    AuthFormSocialloginMutation,
    AuthFormSocialloginMutationVariables
  >(SOCIAL_LOGIN_MUTATION, { context: { clientName: 'auth' } });

  // @ts-ignore
  const googleAuthRef = useRef<gapi.auth2.GoogleAuth>();

  const [state, setState] = useState({
    loadingFacebookAndGoogleScripts: false,
    errorToLoadFacebookAndGoogleScripts: false,
  });

  const _getNextUrl = (): string | undefined => {
    if (getNextUrlProp) {
      return getNextUrlProp();
    }

    return nextUrlProp || getNextUrl();
  };

  const nextUrl = _getNextUrl();

  const login = async (variables: SocialLoginVariables) =>
    socialLogin({
      variables: {
        isJwtAuth,
        token: variables.token,
        nextUrl: variables.nextUrl,
        backend: variables.backend,
        referFriendId,
        friendInvitationId,
      },
    });

  const redirectToFacebookLoginPage = () => {
    window.location.href = `${facebookFallbackUrl}?next=${nextUrl}`;
  };

  const redirectToGoogleLoginPage = () => {
    window.location.href = `${googleFallbackUrl}?next=${nextUrl}`;
  };

  const attachSignin = (element: HTMLButtonElement | null) => {
    if (!element || !googleAuthRef.current) {
      return;
    }

    googleAuthRef.current.attachClickHandler(
      element,
      {},
      googleUser => {
        setProcessingSocialAuth(true);
        login({
          token: googleUser.getAuthResponse(true).access_token,
          nextUrl,
          backend: 'google-oauth2',
          friendInvitationId,
        }).then(response => {
          setProcessingSocialAuth(false);
          if (response?.data?.socialLogin?.success) {
            // eslint-disable-next-line no-unsafe-optional-chaining
            // biome-ignore lint/correctness/noUnsafeOptionalChaining: optional chaining is safe in this context
            const { redirect, isNewUser } = response?.data?.socialLogin;

            if (isNewUser) {
              sendRegisterTrackToGoogleAdsAndAnalytics();
            } else {
              logEvent({
                event: 'Successful SignIn',
                data: {
                  source: 'Google',
                },
              });
            }

            if (onSuccess) {
              onSuccess({
                redirect,
                isNewUser,
                graphqlResponse: response?.data?.socialLogin,
              });
            } else {
              setTimeout(() => {
                window.location.href = redirect || '/';
              }, 0);
            }
          } else {
            redirectToGoogleLoginPage();
          }
        });
      },
      error => {
        console.log('Google error auth', error);
        redirectToGoogleLoginPage();
      },
    );
  };

  const initializeGoogle = () => {
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (
      process.env.NODE_ENV === 'production' &&
      window.location.origin.includes(CREHANA_DOMAIN_PRD)
    )
      window.gapi.load('auth2', () => {
        // Retrieve the singleton for the GoogleAuth library and set up the client.
        googleAuthRef.current = window.gapi.auth2.init({
          client_id: process.env
            .NEXT_PUBLIC_SOCIAL_AUTH_GOOGLE_OAUTH2_KEY as string,
          cookie_policy: 'single_host_origin',
          // Request scopes in addition to 'profile' and 'email'
          // scope: 'additional_scope'
        });
        attachSignin(gmailButtonRef.current);
      });
  };

  const loginWithFacebook = () => {
    // RemoveConditionThatPreventsContinueWithFacebookFloatingButtonToBeShown
    customLocalStorage.removeItem('CREH__continueAsFacebookUserDialog');

    if (hideFacebookLink) {
      redirectToFacebookLoginPage();
      return;
    }

    if (typeof window !== 'undefined' && window.FB) {
      window.FB.getLoginStatus(facebookLoginStatus => {
        const backend = 'facebook';

        if (facebookLoginStatus.status === 'connected') {
          const token = facebookLoginStatus.authResponse.accessToken;

          setProcessingSocialAuth(true);

          login({
            token,
            nextUrl,
            backend,
            friendInvitationId,
          }).then(response => {
            setProcessingSocialAuth(false);
            if (response?.data?.socialLogin?.success) {
              const { redirect, isNewUser } = response.data.socialLogin;

              if (isNewUser) {
                sendRegisterTrackToGoogleAdsAndAnalytics();
              } else {
                logEvent({
                  event: 'Successful SignIn',
                  data: {
                    source: 'Facebook',
                  },
                });
              }

              if (onSuccess) {
                onSuccess({
                  redirect,
                  graphqlResponse: response.data.socialLogin,
                });
              } else {
                window.location.href = redirect || '/';
              }
            } else {
              redirectToFacebookLoginPage();
            }
          });
        } else {
          window.FB.login(
            loginResponse => {
              if (loginResponse.authResponse) {
                const token = loginResponse.authResponse.accessToken;

                setProcessingSocialAuth(true);
                login({
                  token,
                  nextUrl,
                  backend,
                  friendInvitationId,
                }).then(response => {
                  setProcessingSocialAuth(false);
                  if (response?.data?.socialLogin?.success) {
                    const { redirect, isNewUser } = response.data.socialLogin;

                    if (isNewUser) {
                      sendRegisterTrackToGoogleAdsAndAnalytics();
                    } else {
                      logEvent({
                        event: 'Successful SignIn',
                        data: {
                          source: 'Facebook',
                        },
                      });
                    }

                    if (onSuccess) {
                      onSuccess({ redirect, isNewUser });
                    } else {
                      setTimeout(() => {
                        window.location.href = redirect || '/';
                      }, 10);
                    }
                  } else {
                    redirectToFacebookLoginPage();
                  }
                });
              } else {
                redirectToFacebookLoginPage();
              }
            },
            { scope: 'email' },
          );
        }
      });
    }
  };

  function setupAndInitSocialAuthScripts() {
    setState({
      ...state,
      loadingFacebookAndGoogleScripts: true,
    });
    loadGoogleAndFacebookScripts()
      .then(() => {
        if (onFinishToLoadScripts) {
          onFinishToLoadScripts(true);
        }

        setState(prevState => ({
          ...prevState,
          errorToLoadFacebookAndGoogleScripts: false,
          loadingFacebookAndGoogleScripts: false,
        }));
        initializeGoogle();
      })
      .catch((error: unknown) => {
        console.error('Errot to load the facebook and google skd', error);
        if (onFinishToLoadScripts) {
          onFinishToLoadScripts(false);
        }
        setState(prevState => ({
          ...prevState,
          errorToLoadFacebookAndGoogleScripts: true,
          loadingFacebookAndGoogleScripts: false,
        }));
      });
  }

  return {
    setupAndInitSocialAuthScripts,
    authState: {
      ...state,
      processingSocialAuth,
    },
    gmailButtonProps: {
      id: gmailHTMLButtonId.current,
      ref: gmailButtonRef,
    },
    facebookButtonProps: {
      onClick: loginWithFacebook,
    },
  };
};
